import React from 'react';
import i18n from '../../../../../../i18n';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

/** A numbered marker on the x axis, e.g. a timeline note (Growth WP5.4). */
export type ChartMarker = {
  /** The x label it starts at. */
  x: string;
  /** The x label a range ends at; omitted for one point. */
  x2?: string;
  /** The badge shown on the line; the same number names it in the list beside the chart. */
  number: number;
};
import { DRILL_AXIS_TICK, DRILL_GRID } from './colors';

/** Compact Y ticks so Swiss grouping (112'000) does not wrap in a ~56px axis. */
export function compactAxisTick(
  v: number,
  fallback?: (n: number) => string,
): string {
  const abs = Math.abs(v);
  if (abs >= 10_000) return `${Math.round(v / 1000)}k`;
  if (abs >= 1000) {
    const k = +(v / 1000).toFixed(1);
    return `${String(k).replace(/\.0$/, '')}k`;
  }
  return fallback ? fallback(v) : String(v);
}

export function DrillAreaChart({
  series,
  secondarySeries,
  compareSeries,
  labels,
  fmtVal,
  fmtVal2,
  primaryLabel = i18n.t('performance:drillUi.primary'),
  secondaryLabel = i18n.t('performance:drillUi.secondary'),
  compareLabel,
  compareLabels,
  height = 160,
  markers,
}: {
  series: number[];
  secondarySeries?: number[];
  /** Compare-period series, aligned 1:1 with `series` (null: no comparison day). Rendered as a dashed line. */
  compareSeries?: Array<number | null>;
  labels?: string[];
  fmtVal: (v: number) => string;
  fmtVal2?: (v: number) => string;
  primaryLabel?: string;
  secondaryLabel?: string;
  /** Label for the compare series legend chip, e.g. "vs 3 Jun – 30 Jun". */
  compareLabel?: string;
  /**
   * What each compare point is, aligned 1:1 with `compareSeries` ("29 Sept –
   * 5 Oct 2025"). The tooltip names the hovered point by it; `compareLabel`
   * names the whole compare period and is only the fallback.
   */
  compareLabels?: string[];
  height?: number;
  /** Numbered vertical markers (and shaded ranges) on the x axis. */
  markers?: ChartMarker[];
}) {
  const data = series.map((v, i) => ({
    label: labels?.[i] ?? `${i + 1}`,
    primary: v,
    secondary: secondarySeries?.[i],
    compare: compareSeries?.[i],
    compareName: compareLabels?.[i],
  }));
  const hasCompare = !!compareSeries && compareSeries.length === series.length;
  const dual = !!secondarySeries?.length;
  const axisWidth = 56;

  return (
    <div className="ddrill-chart" style={{ height }} onClick={e => e.stopPropagation()}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: markers?.length ? 20 : 8, right: dual ? 8 : 8, bottom: 0, left: 0 }}
        >
          <defs>
            <linearGradient id="ddrillAreaPrimary" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.35} />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid
            stroke={DRILL_GRID}
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey="label"
            tick={DRILL_AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: DRILL_GRID }}
            interval="preserveStartEnd"
            minTickGap={28}
          />
          <YAxis
            yAxisId="primary"
            tick={DRILL_AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={axisWidth}
            tickFormatter={v => compactAxisTick(Number(v), fmtVal)}
          />
          {dual ? (
            <YAxis
              yAxisId="secondary"
              orientation="right"
              tick={DRILL_AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={axisWidth}
              tickFormatter={v =>
                compactAxisTick(Number(v), fmtVal2 ?? fmtVal)
              }
            />
          ) : null}
          <Tooltip
            formatter={(value: number, name: string, item: { payload?: { compareName?: string } }) => {
              if (name === 'secondary' && fmtVal2) {
                return [fmtVal2(value), secondaryLabel];
              }
              if (name === 'compare') {
                return [
                  value == null ? '—' : fmtVal(value),
                  item?.payload?.compareName || compareLabel || i18n.t('performance:drillUi.compare'),
                ];
              }
              return [fmtVal(value), primaryLabel];
            }}
            labelFormatter={l => String(l)}
            contentStyle={{
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              borderRadius: 8,
              fontSize: 12,
            }}
          />
          <Area
            yAxisId="primary"
            type="monotone"
            dataKey="primary"
            name="primary"
            stroke="var(--accent)"
            fill="url(#ddrillAreaPrimary)"
            strokeWidth={2}
            isAnimationActive={false}
          />
          {dual ? (
            <Area
              yAxisId="secondary"
              type="monotone"
              dataKey="secondary"
              name="secondary"
              stroke="var(--petrol-ink)"
              fill="transparent"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              isAnimationActive={false}
            />
          ) : null}
          {(markers || [])
            .filter(marker => marker.x2 && marker.x2 !== marker.x)
            .map(marker => (
              <ReferenceArea
                key={`range-${marker.number}`}
                yAxisId="primary"
                x1={marker.x}
                x2={marker.x2}
                fill="var(--petrol-ink)"
                fillOpacity={0.07}
                strokeOpacity={0}
                ifOverflow="hidden"
              />
            ))}
          {(markers || []).map(marker => (
            <ReferenceLine
              key={`marker-${marker.number}`}
              yAxisId="primary"
              x={marker.x}
              stroke="var(--petrol-ink)"
              strokeDasharray="2 3"
              strokeWidth={1.5}
              ifOverflow="hidden"
              label={{
                value: String(marker.number),
                position: 'top',
                fill: 'var(--petrol-ink)',
                fontSize: 12,
                fontWeight: 700,
              }}
            />
          ))}
          {hasCompare ? (
            <Area
              yAxisId="primary"
              type="monotone"
              dataKey="compare"
              name="compare"
              stroke="var(--ink-3)"
              fill="none"
              strokeWidth={1.5}
              strokeDasharray="5 4"
              dot={false}
              isAnimationActive={false}
            />
          ) : null}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
